import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { dsV2 } from '@bloomays-lib/ui.shared';
import type { ActivityDayDetails } from '@bloomays-lib/types.shared';

/**
 * Grille calendaire d'un mois de CRA (composant autonome, testé via story).
 *
 * 7 colonnes lundi→dimanche, pastille « aujourd'hui ». `dayWorked` est une **fraction de journée**
 * (1 = jour plein, 0.5 = demi, 0.875 = 7h/8h…). Couleurs : présent (vert) > 0.5 j, demi-journée
 * (ambre) > 0, **HNO (rouge clair)** pour une journée travaillée un jour férié ou un week-end,
 * absent (gris) sinon. Un week-end non travaillé est masqué (alignement lun→dim préservé).
 */
export type CraMonthGridProps = {
  days: ActivityDayDetails[];
  /** Date de référence pour la pastille « aujourd'hui » (injectable pour les tests/stories). */
  referenceDate?: Date;
};

const CRA_DAY_TONE = {
  full: { bg: dsV2.color.vertDim, fg: dsV2.color.vert, brd: dsV2.color.vertBrd },
  half: { bg: dsV2.color.amberDim, fg: dsV2.color.amber, brd: dsV2.color.amberBrd },
  hno: { bg: dsV2.color.rougeLight, fg: dsV2.color.rougeDark, brd: dsV2.color.rougeBorder },
  off: { bg: dsV2.color.surface2, fg: dsV2.color.t4, brd: dsV2.color.borderLight },
} as const;

/** Colonne (lundi = 0) à partir d'une date `dd/MM/yyyy`. */
const dayColumn = (date: string): number => {
  const [dd, mm, yyyy] = date.split('/').map(Number);
  return (new Date(yyyy, (mm ?? 1) - 1, dd).getDay() + 6) % 7;
};

const isSameDay = (date: string, reference: Date): boolean => {
  const [dd, mm, yyyy] = date.split('/').map(Number);
  return reference.getDate() === dd && reference.getMonth() + 1 === mm && reference.getFullYear() === yyyy;
};

export type CraDayKind = keyof typeof CRA_DAY_TONE | 'hidden';

/**
 * Tonalité d'un jour de CRA :
 * - HNO (rouge clair) si une journée travaillée tombe un jour férié (`isHoliday`, méthode existante)
 *   ou un week-end ;
 * - présent (vert) > 0.5 j, demi-journée (ambre) > 0, absent (gris) sinon ;
 * - week-end non travaillé → masqué (`hidden`), pour préserver l'alignement lun→dim.
 */
export const craDayTone = (
  day: Pick<ActivityDayDetails, 'dayWorked' | 'isSaturday' | 'isSunday' | 'isHoliday'>,
): CraDayKind => {
  const worked = day.dayWorked ?? 0;
  const weekend = day.isSaturday || day.isSunday;
  if (worked > 0 && (weekend || day.isHoliday)) return 'hno';
  if (weekend) return 'hidden';
  if (worked > 0.5) return 'full';
  if (worked > 0) return 'half';
  return 'off';
};

const LEGEND: { label: string; tone: keyof typeof CRA_DAY_TONE }[] = [
  { label: 'Présent', tone: 'full' },
  { label: '½ journée', tone: 'half' },
  { label: 'HNO', tone: 'hno' },
  { label: 'Absent', tone: 'off' },
];

export const CraMonthGrid = ({ days, referenceDate }: CraMonthGridProps) => {
  if (!days || days.length === 0) return null;
  const reference = referenceDate ?? new Date();
  const cells: (ActivityDayDetails | null)[] = [...Array(dayColumn(days[0].date)).fill(null), ...days];

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px', marginBottom: '11px' }}>
        {cells.map((d, i) => {
          if (!d) return <Box key={`empty-${i}`} />;
          const kind = craDayTone(d);
          // Week-end non travaillé masqué (préserve l'alignement lun→dim) ; un week-end ou férié
          // travaillé reste affiché, en HNO.
          if (kind === 'hidden') return <Box key={d.id} sx={{ aspectRatio: '1', opacity: 0 }} />;
          const tone = CRA_DAY_TONE[kind];
          return (
            <Box
              key={d.id}
              sx={{
                aspectRatio: '1',
                borderRadius: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '9.5px',
                fontFamily: dsV2.font.sansMedium,
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 500,
                background: tone.bg,
                color: tone.fg,
                border: `1px solid ${tone.brd}`,
                ...(isSameDay(d.date, reference)
                  ? { outline: `2px solid ${dsV2.color.bleu}`, outlineOffset: '1px' }
                  : {}),
              }}
            >
              {d.id}
            </Box>
          );
        })}
      </Box>
      <Stack direction="row" spacing="12px" sx={{ flexWrap: 'wrap' }}>
        {LEGEND.map((leg) => {
          const tone = CRA_DAY_TONE[leg.tone];
          return (
            <Stack
              key={leg.label}
              direction="row"
              spacing="5px"
              alignItems="center"
              sx={{ fontSize: 11, color: dsV2.color.t4 }}
            >
              <Box
                sx={{ width: 9, height: 9, borderRadius: '3px', background: tone.bg, border: `1px solid ${tone.brd}` }}
              />
              {leg.label}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export default CraMonthGrid;
